import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ReusableTable from "../../dashboard/ReusableTable";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from "../../common/ReusableDeleteDialog";
import { getRequest, postRequest } from "../../services/apiService";
import { Dropdown } from "primereact/dropdown";
import CompartmentActivityMapping from './CompartmentActivityMapping';
import { X } from "lucide-react";

const Compartment = ({ onBack }) => {
  const toast = useRef(null);
  const [compartments, setCompartments] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivityMappingModal, setShowActivityMappingModal] = useState(false);
  const [currentCompartment, setCurrentCompartment] = useState(null);
  const [compartmentToDelete, setCompartmentToDelete] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [activityMappingLoading, setActivityMappingLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    compt_category: "",
    status_db: 1,
  });

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Delete form state
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const formFields = [
    {
      name: "name",
      label: "Compartment Name",
      type: "text",
      required: true,
      placeholder: "Enter compartment name"
    },
    {
      name: "compt_category",
      label: "Compartment Category",
      type: "select",
      required: true,
      options: [
        { label: "General Compartment", value: "General" },
        { label: "Semi-Clean Compartment", value: "Semi-Clean" },
        { label: "Clean Compartment", value: "Clean" }
      ]
    }
  ];
  const categoryOptions = [
    { label: "", value: "" },
    ...formFields.find(f => f.name === "compt_category").options
  ];
  // Debounced search effect for table search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchCompartments();
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage, selectedCategory]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const fetchActivities = async () => {
    try {
      const response = await getRequest(`master/compartment-activity/?dropdown=true`);
      setActivities(response || []);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch activities",
        life: 3000
      });
    }
  };

  const fetchActivityMappings = async (compartmentId) => {
    setActivityMappingLoading(true);
    try {
      const response = await getRequest(`master/compartment-activity-mapping/?compartment_id=${compartmentId}&dropdown=true`);
      const mappedActivities = response.map(mapping => mapping.compartment_activity.id);
      setSelectedActivities(mappedActivities);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch activity mappings",
        life: 3000
      });
    } finally {
      setActivityMappingLoading(false);
    }
  };

  const saveActivityMappings = async () => {
    setActivityMappingLoading(true);
    try {
      // First fetch existing mappings to get IDs for already mapped activities
      const existingMappingsResponse = await getRequest(
        `master/compartment-activity-mapping/?compartment_id=${currentCompartment.id}&dropdown=true`
      );
      
      // Prepare the payload according to the requirements
      const payload = selectedActivities.map(activityId => {
        // Find if this activity is already mapped
        const existingMapping = existingMappingsResponse.find(
          mapping => mapping.compartment_activity.id === activityId
        );
        
        return {
          id: existingMapping ? existingMapping.id : null,
          compartment_activity: activityId
        };
      });
  
      // If no checkboxes are selected, we still need to send an empty array
      // or the payload will be prepared as empty array when selectedActivities is empty
      const finalPayload = selectedActivities.length === 0 ? [] : payload;
  
      // Use POST request to the specified endpoint
      await postRequest(
        `master/compartment-activity-mapping/${currentCompartment.id}/`,
        finalPayload
      );
      
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Activity mappings updated successfully!",
        life: 3000
      });
      
      setShowActivityMappingModal(false);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update activity mappings",
        life: 3000
      });
    } finally {
      setActivityMappingLoading(false);
    }
  };

  const fetchCompartments = async () => {
    setLoading(true);
    try {
      let url = `master/compartments/?search=${encodeURIComponent(search)}&page=${currentPage}`;
      if (selectedCategory) {
        url += `&compt_category=${encodeURIComponent(selectedCategory)}`;
      }
      const response = await getRequest(url);
      const compartmentsArray = response.results?.data || [];
      setCompartments(compartmentsArray);
      setTotalRecords(response.count || 0);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch compartments",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const addCompartment = async () => {
    try {
      await postRequest("master/compartments/", formData);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Compartment added successfully!",
        life: 3000
      });
      setShowAddModal(false);
      await fetchCompartments();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add compartment",
        life: 3000
      });
    }
  };

  const updateCompartment = async () => {
    try {
      // Using POST for update with id in payload
      await postRequest(`master/compartments/`, {
        ...formData,
        id: currentCompartment.id
      });
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Compartment updated successfully!",
        life: 3000
      });
      setShowEditModal(false);
      await fetchCompartments();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update compartment",
        life: 3000
      });
    }
  };

  const deleteCompartment = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }

    try {
      // Using POST for delete with status_db=3
      await postRequest(`master/compartments/`, {
        id: compartmentToDelete.id,
        status_db: 3
      });
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Compartment deleted successfully!",
        life: 3000
      });
      setShowDeleteModal(false);
      setCompartmentToDelete(null);
      setDeleteFormData({ confirmation: "" });
      await fetchCompartments();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete compartment",
        life: 3000
      });
    }
  };

  const openAddModal = () => {
    setFormData({ name: "", compt_category: "", status_db: 1 });
    setShowAddModal(true);
  };

  const openEditModal = (compartment) => {
    setCurrentCompartment(compartment);
    setFormData({ 
      name: compartment.name, 
      compt_category: compartment.compt_category,
      status_db: compartment.status_db 
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (compartment) => {
    setCompartmentToDelete(compartment);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const openActivityMappingModal = (compartment) => {
    setCurrentCompartment(compartment);
    setSelectedActivities([]);
    fetchActivities();
    fetchActivityMappings(compartment.id);
    setShowActivityMappingModal(true);
  };

  const onActivityToggle = (activityId) => {
    setSelectedActivities((prevSelected) => {
      if (prevSelected.includes(activityId)) {
        return prevSelected.filter(id => id !== activityId);
      } else {
        return [...prevSelected, activityId];
      }
    });
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const columnFields = [
    { field: "name", header: "Name" },
    { field: "code", header: "Code" },
    { field: "compt_category", header: "Category" }
  ];

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditModal(rowData)
    },
    {
      label: "Activity Mapping",
      icon: "pi pi-list",
      command: (rowData) => openActivityMappingModal(rowData)
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData)
    }
  ];

  const activityMappingDialogFooter = (
    <div className="flex justify-end gap-3">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setShowActivityMappingModal(false)}
        className="p-button-text"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={saveActivityMappings}
        loading={activityMappingLoading}
        className="p-button-primary"
      />
    </div>
  );

  const closeAddEditModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
  };

  const closeActivityMappingModal = () => {
    setShowActivityMappingModal(false);
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100">
      <Toast ref={toast} />
      <div className="max-w-full mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Compartment Management</h2>
         
        </div>

        <div className="flex justify-between items-center mb-4">
          <Dropdown 
            value={selectedCategory}
            options={categoryOptions}
            onChange={(e) => {
              setSelectedCategory(e.value);
              setCurrentPage(1);
            }}
            placeholder="Filter by Category"
            className="w-48"
          />
        </div>

        <ReusableTable
          title="Compartments"
          data={compartments}
          setData={setCompartments}
          columnFields={columnFields}
          loading={loading}
          setAddNewDialogVisible={openAddModal}
          actionMenu={true}
          actionItems={actionItems}
          totalRecords={totalRecords}
          rows={rowsPerPage}
          onPageChange={onPageChange}
          first={(currentPage - 1) * rowsPerPage}
          searchValue={search}
          onSearchChange={handleTableSearch}
        />

        {/* Add Modal using ReusableForm with explicit wrapper */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-lg animate-fade-in">
              <ReusableForm
                headerText="Add Compartment"
                formData={formData}
                setFormData={setFormData}
                fields={formFields}
                onSubmit={addCompartment}
                onHide={closeAddEditModal}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Edit Modal using ReusableForm with explicit wrapper */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-lg animate-fade-in">
              <ReusableForm
                headerText="Edit Compartment"
                formData={formData}
                setFormData={setFormData}
                fields={formFields}
                onSubmit={updateCompartment}
                onHide={closeAddEditModal}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Activity Mapping Modal - Custom Overlay (Corrected to preserve its original structure) */}
        {showActivityMappingModal && currentCompartment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"> {/* Retain outer wrapper for AM modal */}
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh]"> {/* Retain inner panel for AM modal */}
              <div className="flex justify-between items-center p-4 bg-[#1e3a8a] text-white rounded-t-lg sticky top-0 z-10">
                <h3 className="text-lg font-semibold">Activity Mapping for: {currentCompartment.name}</h3>
                <button onClick={closeActivityMappingModal} className="text-white hover:text-gray-300">
                  <X size={24} className="text-white" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-grow">
                <CompartmentActivityMapping 
                  compartmentId={currentCompartment.id}
                  onClose={closeActivityMappingModal} // Pass close function if needed internally by component
                />
              </div>
              {activityMappingDialogFooter} {/* Render the existing footer */}
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={deleteCompartment}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Compartment"
          description={`Are you sure you want to delete compartment "${compartmentToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />
      </div>
    </div>
  );
};

export default Compartment;